import { QuoteOrderModelType } from '@teinor/erp/types/company/order/quote/quoteOrder'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import ITHeaderText, { ITHeaderTextCustomer } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableLeftColumnForm from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftColumnChange, InputTableLeftTabsProps } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { returnClassOfSpanTemplate } from '../../../../../../../utils/globals/tailwind'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { parseAddressToOneLine } from '../../../../../../../utils/globals/address'
import { permissionCheck } from '../../../../../../../baseComponents/PermissionChecker/function'
import { getActiveCurrenciesDictionary, getUserPermissions } from '../../../../../../../utils/reducers/getters'
import { WorkingCenterPayload } from '../../../../../../../utils/reducers/company/workingCenters'
import { CustomSelectWithSearchBar } from '../../../../../../../forms/CustomSelect'
import { UseEditDeleteModalProps } from '../../../../../../../utils/hooks/useEditDeleteModal'
import ModalEditOrderCurrency from '../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/TableComponents/ModalsOrderTable/ModalEditOrderCurrency'
import { SellTagsPayload } from '../../../../../../../utils/reducers/sells/sellTags'
import { notHaveCountersWorkingCenter } from '../../../../../../../utils/globals/order/order'
import { sortEmployeesByUser } from '../../../../../../../utils/globals/employees'
import { EmployeesPayload } from '../../../../../../../utils/reducers/company/employees'
import { ITHeaderOrderTags } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components'
import { OrderHeaderURLs } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { CustomSellSelectProject, CustomSellSelectCampaign } from '../../../../../../../specificComponents/sells/components'
import { InputWithLabelMargin } from '../../../../../../../forms/Input'

type QuoteStepHeaderMainProps = InputTableLeftTabsProps & QuoteStepHeaderMainLogicProps & {
    orderHeaderURLs: OrderHeaderURLs
    updateCurrencyURL: string
    sellTags: SellTagsPayload
    employees: EmployeesPayload
    handleChange: InputTableLeftColumnChange
}

const QuoteStepHeaderMain: React.FC<QuoteStepHeaderMainProps> = ({ workingCenters, getCompleteData, handleChange, headerData, updateCurrencyURL, sellTags, employees, ...logic }) => {
    const { t } = useTwinTranslation()
    const { dictQuoteStepHeaderExtraData, activeCurrencies, openModal, setOpenModal, changeCurrencyId, memorizedWorkingCenters, workingCenterAddress, getCustomerCampaigns, getCustomerProjects, customerProjects, customerCampaigns, dictWarehouses } = useQuoteStepHeaderMainLogic({ headerData, workingCenters, ...logic })
    const sections: FormRender<QuoteOrderModelType> = [
        {
            cols: 5,
            elements: [
                {
                    name: 'CustomerId',
                    component: 'Custom',
                    render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData} className={returnClassOfSpanTemplate(5)} showCustomerProjectName={false} />
                },
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: (props) => {
                        return <InputWithLabelMargin name='custom_name' label={t('customName', 'Nombre personalizado')} onChange={props.onChange} defaultValue={headerData.custom_name} className={returnClassOfSpanTemplate(5)}/>
                    }
                },
                {
                    name: 'CustomerProjectId',
                    component: 'Custom',
                    render: ({ extraProps, setLockOnClickOut }) => <CustomSellSelectProject getCompleteData={getCustomerProjects} handleChange={handleChange} headerData={headerData} items={extraProps.customerProjects} classNameField={returnClassOfSpanTemplate(5)} setLockOnClickOut={setLockOnClickOut} />,
                    extraProps: {
                        customerProjects: customerProjects || {}
                    }
                },
                {
                    name: 'CampaignCustomerId',
                    component: 'Custom',
                    render: ({ extraProps, setLockOnClickOut }) => <CustomSellSelectCampaign getCompleteData={getCustomerCampaigns} handleChange={handleChange} headerData={headerData} items={extraProps.customerCampaigns} label={t('campaign', 'Campaña')} classNameField={returnClassOfSpanTemplate(5) + '-mt-15 -mb-8'} setLockOnClickOut={setLockOnClickOut} />,
                    extraProps: {
                        customerCampaigns: customerCampaigns || {}
                    }
                },
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('representative', 'Representante'),
                    items: employees || {},
                    onChange: getCompleteData,
                    className: returnClassOfSpanTemplate(5),
                    sortItems: true,
                    fieldName: 'fullname_short',
                    sortFunction: (emp) => sortEmployeesByUser(emp, false)
                },
                {
                    name: 'processed_date',
                    component: 'InputCalendarStateFull',
                    label: t('date', 'Fecha'),
                    onlyValids: true,
                    className: returnClassOfSpanTemplate(3),
                    required: true
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value } ) => <ITHeaderText label={t('draftN', 'Nº de borrador')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA') + ' ' + returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'WorkingCenterId',
                    component: 'CustomSelectBlockedItemsSearchbar',
                    label: t('workingCenter', 'Centro de trabajo'),
                    items: memorizedWorkingCenters || {},
                    blockItemsFnc: notHaveCountersWorkingCenter ,
                    onChange: getCompleteData,
                    className: returnClassOfSpanTemplate(3),
                    sortItems: true
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie || ''} className={returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'CustomerAddress',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('fiscalAddressCustomer', 'Dirección Fiscal cliente')} value={dictQuoteStepHeaderExtraData?.CustomerAddress || ''} className={returnClassOfSpanTemplate(5)} />
                    },
                    extraProps: {
                        dictQuoteStepHeaderExtraData
                    }
                },
                {
                    name: 'WorkingCenterAddressId',
                    component: 'Custom',
                    render: ({ value }) => {
                        return <ITHeaderText label={t('workingCenterFiscalAddress', 'Dirección Fiscal centro de trabajo')} value={workingCenterAddress || ''} className={returnClassOfSpanTemplate(5)} />
                    },
                    extraProps: {
                        workingCenterAddress
                    }
                },
                {
                    name: 'WarehouseId',
                    component: 'CustomSelectWithSearchBar',
                    label: t('warehouse', 'Almacén'),
                    items: dictWarehouses || {},
                    className: returnClassOfSpanTemplate(5),
                    value: headerData?.WarehouseId || 0
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return <CustomSelectWithSearchBar label={t('currency', 'Moneda')} items={extraProps.activeCurrencies} value={extraProps.CurrencyId} onChange={extraProps.changeCurrencyId} className={returnClassOfSpanTemplate(2)} isStateLess={true} />
                    },
                    extraProps: {
                        activeCurrencies,
                        CurrencyId: headerData.CurrencyId,
                        changeCurrencyId
                    }
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (<ITHeaderOrderTags selectedTags={extraProps.selectedTags} dictTags={sellTags || []} headerData={headerData} onChange={handleChange} {...logic} className={returnClassOfSpanTemplate(5)} />)
                    },
                    extraProps: {
                        selectedTags: headerData?.tags,
                    }
                },
            ]
        }
    ]
    return (
        <div className='flex flex-auto flex-col'>
            <InputTableLeftColumnForm form={sections} handleChange={handleChange} headerData={headerData} />
            {openModal?.type === 'edit' ? 
                <ModalEditOrderCurrency Currency={activeCurrencies?.[openModal?.allRowData?.CurrencyId]?.name} id={headerData?.id} {...{ updateCurrencyURL, onSubmit: getCompleteData, setOpened: () => setOpenModal(null), allRowData: openModal?.allRowData}}/>
                : null
            }
        </div>
    )
}

interface QuoteStepHeaderMainLogicProps {
    headerData: TwinDictionary
    workingCenters: WorkingCenterPayload
}

interface QuoteStepHeaderExtraData {
    CustomerAddress: string | null
}

export const useQuoteStepHeaderMainLogic = ({ workingCenters, headerData }: QuoteStepHeaderMainLogicProps) => {
    const { t } = useTwinTranslation()
    const [openModal, setOpenModal] = useState<UseEditDeleteModalProps>(null)
    const CustomerAddress = headerData.CustomerAddress
    const WorkingCenterAddress = headerData.WorkingCenterAddress
    const WorkingCenterId = headerData.WorkingCenterId
    const CustomerId = headerData.CustomerId
    const CurrencyId = headerData.CurrencyId
    const [customerProjects, setCustomerProjects] = useState<TwinDictionary[]>([{ [headerData?.CustomerProject?.id]: headerData?.CustomerProject }])
    const [customerCampaigns, setCustomerCampaigns] = useState<TwinDictionary[]>([{ [headerData?.CampaignCustomer?.id]: headerData?.CampaignCustomer?.Campaign }])

    const [dictQuoteStepHeaderExtraData, setDictQuoteStepHeaderExtraData] = useState<QuoteStepHeaderExtraData>({ CustomerAddress: CustomerAddress ? parseAddressToOneLine(CustomerAddress) : null})
    const workingCenterAddress = useMemo(() => parseAddressToOneLine(WorkingCenterAddress), [WorkingCenterAddress])
    const anyWarehouseText = t('anyWarehouse', 'Cualquier almacén')
    const dictWarehouses = useMemo(() => ({
        '0': { id: '0', name: anyWarehouseText },
        ...workingCenters?.[parseInt(WorkingCenterId || '1')]?.dictWarehouses
    }), [workingCenters, WorkingCenterId, anyWarehouseText])
    const memorizedWorkingCenters = useMemo(() => {
        const copyWorkingCenters = JSON.parse(JSON.stringify(workingCenters))
        if (headerData.WorkingCenterId && headerData.WorkingCenter && !copyWorkingCenters[headerData.WorkingCenterId]) {
            copyWorkingCenters[headerData.WorkingCenterId] = { id: headerData.WorkingCenterId, name: headerData.WorkingCenter?.name }
        }
        return copyWorkingCenters
    }, [headerData?.WorkingCenterId, headerData?.WorkingCenter, workingCenters])

    const activeCurrencies = getActiveCurrenciesDictionary()
    const getAllCustomerAddress = useCallback(async () => {
        const items = await twinFetchPostJSON('/api/app/customer/customerAddress/getAllCustomerAddresses', { where: { 'CustomerId': CustomerId, 'fiscal_address': true } })
        if (items && items.length) {
            setDictQuoteStepHeaderExtraData((old) => {
                const copy = JSON.parse(JSON.stringify(old))
                return {
                    ...copy,
                    CustomerAddress: parseAddressToOneLine(items[0])
                }
            })
        }
    }, [setDictQuoteStepHeaderExtraData, CustomerId])

    const getCustomerProjects = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/customerProject/getAllCustomerProjects', {
            where:
            {
                'CustomerProject.active': true,
                CustomerId
            },
        })
        if (result) {
            const projects: TwinDictionary[] = []
            for (const element of result) {
                projects.push({ ...element, value: element.id, text: element?.name })
            }
            setCustomerProjects(projects)
        }
    }, [CustomerId])

    const getCustomerCampaigns = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/campaign/customer/getAllCampaignCustomers', {
            where:
            {
                CustomerId
            },
        })
        if (result) {
            const campaigns: TwinDictionary[] = []
            for (const element of result) {
                campaigns.push({ ...element?.Campaign, id: element?.id, value: element.id, text: element?.Campaign?.name })
            }
            setCustomerCampaigns(campaigns)
        }
    }, [CustomerId])

    const changeCurrencyId = useCallback((value: string) => {
        if (value !== CurrencyId){
            setOpenModal({ type: 'edit', allRowData: { CurrencyId: value } })
        }
    }, [setOpenModal, CurrencyId])

    useEffect(() => {
        if (!CustomerAddress?.id && permissionCheck(getUserPermissions('customer.profile.address'), 'read')) {
            getAllCustomerAddress()
        }
    }, [getAllCustomerAddress, CustomerAddress?.id])

    useEffect(() => {
        if (permissionCheck(getUserPermissions('customer.projects.main'), 'read') || permissionCheck(getUserPermissions('customer.profile.projects'), 'read')) {
            getCustomerProjects()
        }
    }, [getCustomerProjects])

    useEffect(() => {
        if (permissionCheck(getUserPermissions('customer.campaigns.main'), 'read') || permissionCheck(getUserPermissions('customer.profile.campaign'), 'read') || permissionCheck(getUserPermissions('customer.campaigns.inner'), 'read')) {
            getCustomerCampaigns()
        }
    }, [getCustomerCampaigns])

    return { getAllCustomerAddress, dictQuoteStepHeaderExtraData, activeCurrencies, openModal, setOpenModal, changeCurrencyId, workingCenterAddress, memorizedWorkingCenters, getCustomerCampaigns, getCustomerProjects, customerProjects, customerCampaigns, dictWarehouses }
}



export default QuoteStepHeaderMain